import { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "../utlis/axiosInstance";

interface ProtocolsContextType {
  protocols: any[];
  loading: boolean;
  error: string | null;
  refreshProtocols: () => Promise<void>;
  clearError: () => void;
}

const ProtocolsContext = createContext<ProtocolsContextType>({
  protocols: [],
  loading: false,
  error: null,
  refreshProtocols: async () => {},
  clearError: () => {},
});

export function ProtocolsProvider({ children }: any) {
  const [user, setUser] = useState<any>(JSON.parse(localStorage.getItem("user") || "{}"));
  const [protocols, setProtocols] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user") {
        const newUser = e.newValue ? JSON.parse(e.newValue) : {};
        setUser(newUser);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get("gpstracker/jsonhanler/", {
        params: {
          limit: 1000,
          offset: 0,
        },
      });

      if (response.data.status && response.data.responseData) {
        console.log(`📡 ProtocolsProvider: Fetched ${response.data.responseData.length} protocols from API`);
        setProtocols(response.data.responseData);
        setError(null);
      } else {
        console.log("❌ ProtocolsProvider: Failed to fetch protocols", response.data);
        setError(response.data?.message || "Failed to fetch protocols data");
      }
    } catch (err) {
      console.error("Error fetching protocols:", err);
      setError(err instanceof Error ? err.message : "An error occurred while fetching protocols data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch protocols when user is logged in
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      fetchProtocols();
    } else {
      setProtocols([]);
      setLoading(false);
      setError(null);
    }
  }, [user]);

  const contextValue: ProtocolsContextType = {
    protocols,
    loading,
    error,
    refreshProtocols: fetchProtocols,
    clearError: () => setError(null)
  };

  return (
    <ProtocolsContext.Provider value={contextValue}>
      {children}
    </ProtocolsContext.Provider>
  );
}

export const useProtocols = () => {
  const context = useContext(ProtocolsContext);
  if (!context) {
    throw new Error("useProtocols must be used within a ProtocolsProvider");
  }
  return context;
};
